import { scaleById, type Scale } from './scales.js';

export const A4_HZ = 440;
export const midiToHz = (midi: number): number => A4_HZ * Math.pow(2, (midi - 69) / 12);
export const hzToMidi = (hz: number): number => 69 + 12 * Math.log2(hz / A4_HZ);
export const centsBetween = (fromHz: number, toHz: number): number => 1200 * Math.log2(toHz / fromHz);

/** Sa of octave 0 of the mandala is Sa3 (C#3 = MIDI 49 when tonic = C#). Ring R0 is octave -1, R3 is octave +2. */
export const SA_OCTAVE0_MIDI_C = 48;

/** A pitch on the mandala: which degree, which octave (relative to Sa3), and a bend in cents. */
export interface PitchSpec {
  degree: number;       // 0..N (N = Sa' closing the ring)
  octave: number;       // -1..2
  bendCents: number;    // -45..45
}

export interface TuningContext {
  tonicPc: number;      // 0..11 pitch class of Sa (0 = C)
  scale: Scale;
  ji: boolean;          // just intonation (true) or 12-TET
}

export const saHz = (tonicPc: number, octave = 0): number => midiToHz(SA_OCTAVE0_MIDI_C + tonicPc + 12 * octave);

/** Frequency of a degree of the scale (degree N == Sa an octave up). */
export function degreeHz(ctx: TuningContext, degree: number, octave: number, bendCents = 0): number {
  const n = ctx.scale.semitones.length;
  const wrapped = ((degree % n) + n) % n;
  const extraOct = Math.floor(degree / n);
  const base = saHz(ctx.tonicPc, octave + extraOct);
  const ratio = ctx.ji ? ctx.scale.ratios[wrapped]! : Math.pow(2, ctx.scale.semitones[wrapped]! / 12);
  return base * ratio * Math.pow(2, bendCents / 1200);
}

export const pitchSpecHz = (ctx: TuningContext, p: PitchSpec): number => degreeHz(ctx, p.degree, p.octave, p.bendCents);

/** Equivalent 12-TET semitone offset from Sa3 (fractional), used for glide-time and multisample math. */
export function pitchSpecSemitones(ctx: TuningContext, p: PitchSpec): number {
  return 12 * Math.log2(pitchSpecHz(ctx, p) / saHz(ctx.tonicPc, 0));
}

/** Glide law: 60 ms per semitone, capped at 1.2 s. Returns seconds. */
export const glideSeconds = (semitonesMoved: number): number => Math.min(1.2, 0.06 * Math.abs(semitonesMoved));

/** Time constant for setTargetAtTime that reaches ~99% in `seconds`. */
export const tauFor = (seconds: number): number => Math.max(0.005, seconds / 4.6);

export function makeTuning(tonicPc: number, scaleId: string, ji = true): TuningContext {
  return { tonicPc, scale: scaleById(scaleId), ji };
}

/** Semitone interval class (0..11) between two frequencies. */
export const intervalClass = (aHz: number, bHz: number): number => {
  const st = Math.round(12 * Math.log2(bHz / aHz));
  return ((st % 12) + 12) % 12;
};
